import PropTypes from 'prop-types';
import { Box, Text, Slider } from '@mantine/core';
import { formatNumberWithCommas } from '@/helpers/string';
import { removeNumberFormatting } from '@/helpers/number';
import classes from './amountSlider.module.css';

const AmountSlider = ({
  value,
  onChange,
  min = 200000,
  max = 300000,
  step = 10000,
  // marks = [],
  marksStep = 100000,
  size = 'xl',
  showValue = true,
  valuePrefix = '',
  valueSuffix = ' ש״ח',
  showYearBox = false,
  yearBoxText = '',
  className,
  ...sliderProps
}) => {
  // Create marks: tick for every step, label only on min/max + 2 equally spaced in between
  const range = max - min;

  const rawLabelPoints = [
    min,
    min + range / 3,
    min + (2 * range) / 3,
    max,
  ];

  // snap to nearest step so the labels land on real slider values
  const snappedLabelPoints = rawLabelPoints.map((v) => {
    const snapped = min + Math.round((v - min) / marksStep) * marksStep;
    return Math.min(max, Math.max(min, snapped));
  });

  const labelSet = new Set(snappedLabelPoints);

  const defaultMarks = [];
  for (let i = min; i <= max; i += marksStep) {
    defaultMarks.push({
      value: i,
      label: labelSet.has(i) ? formatNumberWithCommas(i) : undefined,
    });
  }

  const clamp = (n, a, b) => Math.min(b, Math.max(a, n));
  const handleSliderChange = (newValue) => {
    const safeValue = clamp(newValue, min, max);
    const formattedValue = formatNumberWithCommas(safeValue.toString());
    onChange(formattedValue);
  };

  const numericValue = typeof value === 'string' ? Number(removeNumberFormatting(value)) : Number(value);

  return (
    <Box className={`${classes.periodRow} ${className || ''}`}>
      <Box className={classes.sliderContainer}>
        <Slider
          value={numericValue}
          onChange={handleSliderChange}
          min={min}
          max={max}
          step={step}
          marks={defaultMarks}
          {...sliderProps}
        />
      </Box>

      {showValue && (
        <Box className={classes.yearBox}>
          <Text className={classes.yearText}>
            {valuePrefix}{formatNumberWithCommas(numericValue)} {valueSuffix}
          </Text>
        </Box>
      )}

      {showYearBox && (
        <Box className={classes.yearBox}>
          <Text className={classes.yearText}>
            {yearBoxText}
          </Text>
        </Box>
      )}
    </Box>
  );
};

AmountSlider.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  marksStep: PropTypes.number,
  marks: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.number,
    label: PropTypes.string,
  })),
  size: PropTypes.string,
  showValue: PropTypes.bool,
  valuePrefix: PropTypes.string,
  valueSuffix: PropTypes.string,
  showYearBox: PropTypes.bool,
  yearBoxText: PropTypes.string,
  className: PropTypes.string,
};

export default AmountSlider;
